import React, { useState, useEffect } from 'react';
import { CameraController, CameraViews } from '../../camera-controller.js';
import { InteriorConfigurator } from '../../configurator/interior-configurator.js';
import { VehicleView } from '../vehicle-view.js';
import { LeatherView } from './materials/leather-view.js';
import { SeatsView } from './materials/seats-view.js';
import { StitchingView } from './materials/stitching-view.js';
import { CarpetView } from './materials/carpet-view.js';
import { DashboardView } from './cockpit/dashboard-view.js';
import { ConsoleAndLowerRailView } from './cockpit/console-and-lower-rail.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

enum Views {
	Interior,
	Leather,
	Seats,
	Stitching,
	Carpet,
	Dashboard,
	ConsoleAndLowerRail
}

enum MaterialViewOptions {
	Leather,
	Seats,
	Stitching,
	Carpet,
	Dashboard,
	ConsoleAndLowerRail
}

interface InteriorViewProps {
	cameraController: CameraController;
	interiorConfigurator: InteriorConfigurator;
	onBackButtonClicked: () => void;
	onNextButtonClicked: () => void;
}

interface MainViewProps {
	onOptionClicked: (option: MaterialViewOptions) => void;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function InteriorView(props: InteriorViewProps) {

	// states.
	const [view, setView] = useState<Views>(Views.Interior);

	// hide back button when a "child" section is selected.
	useEffect(() => {
		const prevBtn = document.getElementById('vehicle-summary-prev');
		if (!prevBtn) return;

		if (view !== Views.Interior) {
			prevBtn.style.display = 'none';
		} else {
			prevBtn.style.display = 'block';
		}
	}, [view]);

	// return the HTML.
	switch (view) {
		case Views.Interior:

			// transition the camera to the relevant viewpoint.
			props.cameraController.changeCameraView(CameraViews.InteriorView);

			// return the HTML.
			return (
				<VehicleView
					onBackButtonClicked={props.onBackButtonClicked}
					onNextButtonClicked={props.onNextButtonClicked}>
					<MainView
						onOptionClicked={(option: MaterialViewOptions) => {
							switch (option) {
								case MaterialViewOptions.Leather:
									setView(Views.Leather);
									break;
								case MaterialViewOptions.Seats:
									setView(Views.Seats);
									break;
								case MaterialViewOptions.Stitching:
									setView(Views.Stitching);
									break;
								case MaterialViewOptions.Carpet:
									setView(Views.Carpet);
									break;
								case MaterialViewOptions.Dashboard:
									setView(Views.Dashboard);
									break;
								case MaterialViewOptions.ConsoleAndLowerRail:
									setView(Views.ConsoleAndLowerRail);
									break;
							}
						}}
					/>
				</VehicleView>
			);

		case Views.Leather:

			// transition the camera to the relevant viewpoint.
			props.cameraController.changeCameraView(CameraViews.LeatherView);

			// return the HTML.
			return (
				<LeatherView
					interiorConfigurator={props.interiorConfigurator}
					onBackButtonClicked={() => {
						setView(Views.Interior);
					}}
				/>
			);

		case Views.Seats:

			// transition the camera to the relevant viewpoint.
			props.cameraController.changeCameraView(CameraViews.SeatsView);

			// return the HTML.
			return (
				<SeatsView
					interiorConfigurator={props.interiorConfigurator}
					cameraController={props.cameraController}
					onBackButtonClicked={() => {
						setView(Views.Interior);
					}}
				/>
			);

		case Views.Stitching:

			// transition the camera to the relevant viewpoint.
			props.cameraController.changeCameraView(CameraViews.SeatsView);

			// return the HTML.
			return (
				<StitchingView
					interiorConfigurator={props.interiorConfigurator}
					cameraController={props.cameraController}
					onBackButtonClicked={() => {
						setView(Views.Interior);
					}}
				/>
			);

		case Views.Carpet:

			// transition the camera to the relevant viewpoint.
			props.cameraController.changeCameraView(CameraViews.CarpetView);

			// return the HTML.
			return (
				<CarpetView
					interiorConfigurator={props.interiorConfigurator}
					onBackButtonClicked={() => {
						setView(Views.Interior);
					}}
				/>
			);

		case Views.Dashboard:

			// transition the camera to the relevant viewpoint.
			props.cameraController.changeCameraView(CameraViews.DashboardView);

			// return the HTML.
			return (
				<DashboardView
					interiorConfigurator={props.interiorConfigurator}
					cameraController={props.cameraController}
					onBackButtonClicked={() => {
						setView(Views.Interior);
					}}
				/>
			);

		case Views.ConsoleAndLowerRail:

			// transition the camera to the relevant viewpoint.
			props.cameraController.changeCameraView(CameraViews.ConsoleView);

			// return the HTML.
			return (
				<ConsoleAndLowerRailView
					interiorConfigurator={props.interiorConfigurator}
					onBackButtonClicked={() => {
						setView(Views.Interior);
					}}
				/>
			);

		default:
			return null;
	}
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

function MainView(props: MainViewProps) {
	return (
		<div id="view-content">
			<div id="view-content-header">
				<h2 id="ga4-interior-options" style={{ marginBottom: 0 }}>
					INTERIOR OPTIONS
				</h2>
			</div>
			<div className="view-content-options-rows">
				<div className="view-content-options-row">
					<a role="button" className="view-content-options-item"
						onClick={() => {
							props.onOptionClicked(MaterialViewOptions.Leather);
						}}>
						<img src="assets/images/configuration-items/interior/options/leather.webp" width="100%" height="auto" alt="Leather" />
						<h4>UPHOLSTERY</h4>
					</a>
					<a role="button" className="view-content-options-item"
						onClick={() => {
							props.onOptionClicked(MaterialViewOptions.Seats);
						}}>
						<img src="assets/images/configuration-items/interior/options/seats.webp" width="100%" height="auto" alt="Seat" />
						<h4>SEATS</h4>
					</a>
				</div>
				<div className="view-content-options-row">

					<a role="button" className="view-content-options-item"
						onClick={() => {
							props.onOptionClicked(MaterialViewOptions.Stitching);
						}}>
						<img src="assets/images/configuration-items/interior/options/stitching.webp" width="100%" height="auto" alt="Stitching" />
						<h4>STITCHING</h4>
					</a>

					<a role="button" className="view-content-options-item"
						onClick={() => {
							props.onOptionClicked(MaterialViewOptions.Carpet);
						}}>
						<img src="assets/images/configuration-items/interior/options/carpet.webp" width="100%" height="auto" alt="Carpet" />
						<h4>CARPET</h4>
					</a>
				</div>
				<div className="view-content-options-row">
					<a role="button" className="view-content-options-item"
						onClick={() => {
							props.onOptionClicked(MaterialViewOptions.Dashboard);
						}}>
						<img src="assets/images/configuration-items/interior/options/dashboard.webp" width="100%" height="auto" alt="Dashboard" />
						<h4>DASHBOARD</h4>
					</a>
					<a role="button" className="view-content-options-item"
						onClick={() => {
							props.onOptionClicked(MaterialViewOptions.ConsoleAndLowerRail);
						}}>
						<img src="assets/images/configuration-items/interior/options/lower-rail.webp" width="100%" height="auto" alt="Lower Rail" />
						<h4>COCKPIT WOOD</h4>
					</a>
				</div>
			</div>
		</div>
	);
}
